import apiService, { ServiceType } from './api';

/**
 * The trading block, from the owner's side.
 *
 * Everything the platform does about a restriction is a REFUSAL — the config save, the
 * deposit, the import, the bot's next buy. None of that explains itself, so without this the
 * first a user hears of a window is an edit that fails.
 *
 * UNITS: every money figure here is RAW VND, the unit the API persists in. The engine's own
 * unit is thousand VND and that conversion happens on the server. See docs/MONEY-CONCEPTS.md.
 */

export type RestrictionSource = 'ADMIN' | 'CORPORATE_ACTION';

/** When the sell is armed. See `AUTO_SELL_WHEN_LABELS` for what each means to the user. */
export type AutoSellWhen = 'NOW' | 'NEXT_START';

export interface ExRightsStatus {
    instrumentConfigId: string;
    exchange: string;
    symbol: string;
    /** Shares the wallet claims. Zero: there is nothing to sell or hand over. */
    heldQty: number;
    /** Written to be read: it is the explanation, not a code to look up. */
    reason: string;
    source: RestrictionSource;
    groupId: string | null;
    /** When the block lifts, ISO. Null for open-ended. */
    endsAt: string | null;
    /**
     * When shares still held are handed to the basic book, ISO.
     *
     * NULL MEANS NOBODY IS FORCED OUT. It is not "no date set yet": a ban with no deadline
     * stops new positions and leaves the holder alone, which is a different thing to tell
     * somebody than a date they have to act before.
     */
    retireAfter: string | null;
    /** Whether selling out first is on the table at all — it needs an ex-date to precede. */
    canAutoSell: boolean;
    autoSell: { when: AutoSellWhen; applied: boolean } | null;
}

const core = () => apiService.getService(ServiceType.CORE);

export async function getExRightsStatus(dataConfigId: string): Promise<ExRightsStatus[]> {
    const { data } = await core().get<ExRightsStatus[]>(`/data-configs/${dataConfigId}/ex-rights`);
    return data;
}

/**
 * Ask for the position to be sold out before the ex-date.
 *
 * Throws when no window is open — there would be nothing to sell ahead of, and the
 * instruction would sit waiting to liquidate at some unrelated future start.
 */
export async function requestAutoSell(
    instrumentConfigId: string,
    when: AutoSellWhen,
): Promise<{ groupId: string; applied: boolean }> {
    const { data } = await core().post<{ groupId: string; applied: boolean }>(
        `/instrument-configs/${instrumentConfigId}/ex-rights-auto-sell`,
        { when },
    );
    return data;
}
